import React, { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import axios from "axios";
import axiosRetry from "axios-retry";
import ReactGA from "react-ga";

import {
  loadToken,
  logoutUser,
  setUser,
  setMessages,
} from "../../redux/actions";
import { API_URL, GA_ID } from "../../config/Config";

// Components
import Header from "../../components/header/Header";
import FooterPage from "../../components/footer/Footer";
import FooterUser from "../../components/footer/FooterUser";
import PageLoader from "../../components/loader/PageLoader";
import PublicRoute from "../../components/misc/PublicRoute";
import PrivateRoute from "../../components/misc/PrivateRoute";
import BugReport from "../../components/bugReport/BugReport";
import ScrollReset from "../../components/ScrollReset";

// Lazy-loaded Pages
const Home = lazy(() => import("../home/Home"));
const NotFound = lazy(() => import("../misc/NotFound"));
const ContactUs = lazy(() => import("../pages/ContactUs"));
const TermsOfUse = lazy(() => import("../pages/TermsOfUse"));
const About = lazy(() => import("../pages/About"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const Rules = lazy(() => import("../pages/Rules"));
const RulesOfUse = lazy(() => import("../pages/RulesOfUse"));
const Licences = lazy(() => import("../pages/Licences"));
const Help = lazy(() => import("../pages/Help"));
const BrandHelp = lazy(() => import("../pages/BrandHelp"));
const Philosophy = lazy(() => import("../pages/Philosophy"));
const OurStories = lazy(() => import("../pages/OurStories"));
const DevelopmentModel = lazy(() => import("../pages/DevelopmentModel"));
const Missions = lazy(() => import("../pages/Missions"));
const ForManagers = lazy(() => import("../pages/ForManagers"));
const WhatDo = lazy(() => import("../pages/WhatDo"));

const FaqHOC = lazy(() => import("../faqs/FaqHOC"));
const BlogsHOC = lazy(() => import("../blog/BlogsHOC"));
const Blog = lazy(() => import("../blog/Blog"));
const BlogsByCategory = lazy(() => import("../blog/BlogsByCategory"));
const NewsHOC = lazy(() => import("../news/NewsHOC"));
const NewsDetails = lazy(() => import("../news/NewsDetails"));
const NewsByCategory = lazy(() => import("../news/NewsByCategory"));

const Brands = lazy(() => import("../brands/Brands"));
const Bazar = lazy(() => import("../brands/BazarHOC"));
const LocalBazar = lazy(() => import("../brands/LocalBazar"));
const Brand = lazy(() => import("../brands/BrandHOC"));

const Complains = lazy(() => import("../complains/ComplainsHOC"));
const Complain = lazy(() => import("../complains/Complain"));
const AddThanks = lazy(() => import("../complains/thanks/AddThanks"));
const AddComplain = lazy(() => import("../complains/complain/AddComplain"));

const SearchPage = lazy(() => import("../search/SearchPage"));

const Register = lazy(() => import("../auth/Register"));
const Login = lazy(() => import("../auth/Login"));
const ResetPassword = lazy(() => import("../auth/ResetPassword"));

const Users = lazy(() => import("../user/Users"));
const Massages = lazy(() => import("../user/Massages"));
const PersonalInformation = lazy(() => import("../user/PersonalInformation"));
const MyComments = lazy(() => import("../user/MyComments"));
const MyComplains = lazy(() => import("../user/MyComplains"));
const MyComplaintsDetails = lazy(() => import("../user/MyComplainsDetails"));

const Company = lazy(() => import("../company/Company"));

// Axios retry
axiosRetry(axios, { retries: 3 });

const App = ({ location }) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(true);

  // Google Analytics
  useEffect(() => {
    ReactGA.initialize(GA_ID, { debug: false });
    ReactGA.pageview(window.location.href);
  }, []);

  // Check user token & fetch profile
  useEffect(() => {
    const checkUser = async () => {
      try {
        const apiToken = await loadToken();
        if (!apiToken) {
          dispatch(logoutUser());
        } else {
          const { data, status } = await axios.get(`${API_URL}profile`, {
            headers: {
              Authorization: `Bearer ${apiToken}`,
              Accept: "application/json;charset=UTF-8",
              "Content-Type": "application/json",
              "Accept-Language": "fa",
            },
          });
          if (status === 200) {
            dispatch(setUser(data.data));
            dispatch(setMessages(data.data.messages));
          } else {
            dispatch(logoutUser());
          }
        }
      } catch (error) {
        dispatch(logoutUser());
      } finally {
        setIsLoading(false);
      }
    };

    if (isLoading) checkUser();
  }, [isLoading, dispatch]);

  if (isLoading) {
    return <PageLoader type="spinningBubbles" color="#00b9d7" />;
  }

  return (
    <Router basename="/">
      <ScrollReset location={location} />
      <Suspense fallback={<PageLoader type="bubbles" color="#00b9d7" />}>
        <>
          <Header />
          <div className="appContent">
            <Switch>
              {/* Public Pages */}
              <Route exact path="/" component={Home} />
              <Route path="/contact" component={ContactUs} />
              <Route path="/terms-of-use" component={TermsOfUse} />
              <Route path="/about" component={About} />
              <Route path="/privacy-policy" component={PrivacyPolicy} />
              <Route path="/rules" component={Rules} />
              <Route path="/rules-of-use" component={RulesOfUse} />
              <Route path="/licences" component={Licences} />
              <Route path="/help" component={Help} />
              <Route path="/brand-help" component={BrandHelp} />
              <Route path="/philosophy" component={Philosophy} />
              <Route path="/our-stories" component={OurStories} />
              <Route
                path="/conceptual-development-model"
                component={DevelopmentModel}
              />
              <Route path="/missions" component={Missions} />
              <Route path="/for-managers" component={ForManagers} />
              <Route path="/what-do" component={WhatDo} />

              <Route path="/faqs" component={FaqHOC} />
              <Route path="/blogs" component={BlogsHOC} />
              <Route path="/blog/:slug" component={Blog} />
              <Redirect from="/blog" to="/blogs" exact />
              <Route path="/blog-category/:id" component={BlogsByCategory} />
              <Route path="/news-list" component={NewsHOC} />
              <Route path="/news/:id" component={NewsDetails} />
              <Route path="/news-category/:id" component={NewsByCategory} />

              <Route path="/complains" component={Complains} />
              <Route path="/complain/:id" component={Complain} />
              <Redirect from="/complain" to="/complains" exact />
              <Route path="/add-review" component={AddThanks} />
              <Route path="/add-complaint" component={AddComplain} />

              <Route path="/brands" component={Brands} />
              <Route path="/brand/:slag" component={Brand} />
              <Redirect from="/brand" to="/brands" exact />
              <Route path="/bazar/:id" component={Bazar} />
              <Route path="/local-bazar/:id" component={LocalBazar} />

              <Route path="/search/:query" component={SearchPage} />

              {/* Public Routes */}
              <PublicRoute
                path="/login"
                isAuthenticated={isAuthenticated}
                component={Login}
              />
              <PublicRoute
                path="/register"
                isAuthenticated={isAuthenticated}
                component={Register}
              />
              <PublicRoute
                path="/forgot-password/:token?"
                isAuthenticated={isAuthenticated}
                component={ResetPassword}
              />

              {/* Private Routes */}
              <PrivateRoute
                path="/users"
                isAuthenticated={isAuthenticated}
                component={Users}
              />
              <PrivateRoute
                path="/massages"
                isAuthenticated={isAuthenticated}
                component={Massages}
              />
              <PrivateRoute
                path="/my-complains"
                isAuthenticated={isAuthenticated}
                component={MyComplains}
              />
              <PrivateRoute
                path="/my-complains-details/:id"
                isAuthenticated={isAuthenticated}
                component={MyComplaintsDetails}
              />
              <PrivateRoute
                path="/my-comments"
                isAuthenticated={isAuthenticated}
                component={MyComments}
              />
              <PrivateRoute
                path="/personal-information"
                isAuthenticated={isAuthenticated}
                component={PersonalInformation}
              />

              <Route path="/register-brands" component={Company} />
              <Route component={NotFound} />
            </Switch>
          </div>
          <footer className="page-footer pt-4">
            <FooterPage />
            <FooterUser />
            <BugReport />
          </footer>
        </>
      </Suspense>
    </Router>
  );
};

export default App;
